import React, { useState } from 'react';
import styles from '../styles/PartnerOffers.module.scss';
import { ChevronRight, Star } from 'react-feather';
import { CheckCircle } from 'components/icons';
import { Button } from 'components/quashed';
import { isPurchasedOffer } from '../utils';
import { useSelector } from 'react-redux';
import { IAppState, IEmployer, IUser } from 'types';
import history from 'helpers/history';
import CoversForm from './CoverForm';
import { AIAOffers } from './AIAOffers';
import { windowScrollToId } from 'helpers';
import { MarketScanIcon } from 'components/svgs/MarketScanIcon';
import { LifeIcon } from 'components/svgs/LifeIcon';
import { HealthIcon } from 'components/svgs/HealthIcon';
import classnames from 'classnames';

const q4eIconMap = {
  life: LifeIcon,
  health: HealthIcon,
  star: Star,
  marketScan: MarketScanIcon,
};

const completeCheckFunctionMap = {
  cashback: isPurchasedOffer,
  marketScan: () => false,
  form: ({ questions, user }: { questions: any[]; user: IUser }) =>
    !questions.find((q) => !user[q.key]),
};

const additionalSmallIconClassnames = {
  marketScan: styles.marketScanIcon,
};

interface IProps {
  employer: IEmployer;
  offers: {
    iconName: string;
    iconText: string;
    imageSrc: string;
    key: string;
    link: string;
    subtitle: string;
    title: string;
    redirect: boolean;
    formImage: string;
    formTitle: string;
    questions: any[];
  }[];
  submitEmployer(u: IUser): void;
}

interface OfferCardProps {
  offer: any;
  index: number;
  selectedOfferIndex: number;
  setSelectedOfferIndex: (index: number) => void;
  user: IUser;
  policyScans: any[];
}

const OfferCard: React.FC<OfferCardProps> = ({
  offer,
  index,
  selectedOfferIndex,
  setSelectedOfferIndex,
  user,
  policyScans,
}) => {
  const {
    iconName,
    iconText,
    imageSrc,
    key,
    subtitle,
    title,
    redirect,
    link,
    questions = [],
  } = offer;
  const SmallIcon = q4eIconMap[iconName];
  const smallIconClassName = additionalSmallIconClassnames[iconName];

  const isComplete = completeCheckFunctionMap[key]({
    policyScans,
    user,
    questions,
  });
  const isActive = index === selectedOfferIndex;

  const handleOnSelect = () => {
    if (redirect) {
      history.push(link);
    } else {
      setSelectedOfferIndex(index);
    }
  };

  return (
    <div
      className={classnames(styles.offerCard, isActive && styles.active)}
      onClick={handleOnSelect}
    >
      <div className={styles.iconRow}>
        <SmallIcon
          className={classnames(styles.smallIcon, smallIconClassName)}
        />
        {iconText}
      </div>
      <div className={styles.title}>{title}</div>
      <div className={styles.subTitle}>{subtitle}</div>
      <div className={styles.footer}>
        {isComplete ? (
          <div className={styles.complete}>
            <CheckCircle className={styles.check} /> Added
          </div>
        ) : (
          !isActive && (
            <>
              <div className={styles.fakeLink}>
                {redirect ? 'GET STARTED' : 'LEARN MORE'}
              </div>
              <ChevronRight className={styles.chevronRight} />
            </>
          )
        )}
        <img src={imageSrc} alt={key} className={styles.img} loading="lazy" />
      </div>
    </div>
  );
};

interface OfferDescriptionProps {
  selectedOffer: any;
  policyScans: any[];
  user: IUser;
}

const OfferDescription: React.FC<OfferDescriptionProps> = ({
  selectedOffer,
  policyScans,
  user,
}) => {
  const {
    title,
    images,
    bullets,
    mainImgSrc,
    mainImgAlt,
    subImgText,
    subImgSrc,
    subImgAlt,
    trackingEvent,
  } = selectedOffer.description;

  const isComplete = completeCheckFunctionMap[selectedOffer.key]({
    policyScans,
    user,
  });

  return (
    <div id="q4e-anchor" className={styles.detailsContainer}>
      {mainImgSrc && (
        <div className={styles.bottomImages}>
          {subImgSrc && (
            <div className={subImgText ? styles.subImgWithText : undefined}>
              {subImgText && (
                <div className={styles.subImgText}>{subImgText}</div>
              )}
              <img src={subImgSrc} alt={subImgAlt} loading="lazy" />
            </div>
          )}
          <img src={mainImgSrc} alt={mainImgAlt} loading="lazy" />
        </div>
      )}
      <div className={styles.title}>{title}</div>
      {bullets?.map((point) => (
        <li className={styles.point} key={point}>
          {point}
        </li>
      ))}
      {images && <AIAOffers images={images} />}
      <div className={styles.footer}>
        {!isComplete && (
          <Button
            text="Continue"
            link={selectedOffer.link}
            className={styles.button}
            eventName={trackingEvent}
          />
        )}
      </div>
    </div>
  );
};

const PartnerOffers: React.FC<IProps> = ({
  employer,
  offers,
  submitEmployer,
}) => {
  const [selectedOfferIndex, setSelectedOfferIndex] = useState(0);
  const selectedOffer = offers[selectedOfferIndex];

  const {
    policies: { policies, policyScans },
    user,
  } = useSelector((state: IAppState) => state);

  const handleCardSelect = (idx: number) => {
    setSelectedOfferIndex(idx);
    document.querySelector('#q4e-anchor')?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
    windowScrollToId('q4e-anchor');
  };

  return (
    <div className={styles.offersContainer}>
      <p>Click to learn more</p>
      <div className={styles.offerCardContainer}>
        {offers.map((offer, idx) => (
          <OfferCard
            offer={offer}
            index={idx}
            selectedOfferIndex={selectedOfferIndex}
            setSelectedOfferIndex={handleCardSelect}
            key={offer.key}
            policyScans={policyScans}
            user={user}
          />
        ))}
      </div>
      {selectedOffer && selectedOffer.key === 'form' ? (
        <CoversForm
          {...selectedOffer}
          employer={employer}
          submitEmployer={submitEmployer}
          policies={policies}
          user={user}
        />
      ) : (
        <OfferDescription
          selectedOffer={selectedOffer}
          policyScans={policyScans}
          user={user}
        />
      )}
    </div>
  );
};

export default PartnerOffers;
